import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'

import { ApiError, apiFetch, markBilled } from '../lib/api'

// The legacy Open button routes by study type AND permission
// (`MainStudyList.aspx.vb:536`): a transcriptionist goes to the interpretation
// form, a reading physician to the report hub, everyone else to Study
// Information. Two of those branches have no `Case Else`, so for the remaining
// study types the legacy button does nothing; those fall through to Study
// Information, which is the assignment hub and the handler's own Else branch.
// The server resolves all of it.
interface OpenTarget { kind: string; path: string; reason: string; study_type: string | null }

// Worklist — the signed-in landing page.
//
// Behavioural specification: `oldcode/MainStudyList.aspx` (grid columns, page
// size, sort) and `MainStudyList.aspx.vb` (defaults, column visibility).
//
// Commands: Open, and Billed. Complete lives on the report hub. Send Report is
// out of scope (DEC-013) and Delete is not reproduced (DEC-017) — the legacy
// draws its column for two hardcoded accounts, `BindDataGrid` :316, and it is
// not a user workflow.
//
// The Billed column is shown only when `/api/v1/worklist/options` says the
// legacy would draw it — `HasPermission(2)`, per `BindDataGrid` :322-328. That
// is presentation only: `POST /api/v1/studies/{id}/billed` requires the same
// permission itself, because in the legacy the drawn button *was* the check.
//
// Filter state lives in the URL rather than in session. The legacy kept it in
// ~19 session keys, which is why its worklist could not be bookmarked, leaked
// paging position between four unrelated grids, and lost state on an app-pool
// recycle. Putting it in the query string keeps state across navigation —
// requirement 7 — and makes the view shareable.

interface Option { id: number; name: string }
// `ddlEsign` binds the physician's signature *file*, not their id
// (`MainStudyList.aspx.vb:225`), because the predicate matches `Esign1.IF1`.
interface EsignOption { value: string; name: string }
interface Columns {
  complete: boolean; billed: boolean
  send_report: boolean; view_online: boolean
  status: boolean; study_transfer_date: boolean; device: boolean
}
interface Options {
  facilities: Option[]; study_types: Option[]; statuses: Option[]
  reading_physicians: Option[]; referring_physicians: Option[]
  reviewing_physicians: Option[]; technicians: Option[]; esign: EsignOption[]
  default_facility_id: number; default_status_id: number
  columns: Columns
}
interface Row {
  id: number
  study_datetime: string | null; study_date: string | null
  device_name: string | null; patient_id: string | null; patient_name: string | null
  study_type: string | null; reading_physician: string | null
  status: string | null; billed: boolean | null
  electronically_signed: boolean | null; esigned_by: string | null
  referring_physician: string | null
}
interface Page { rows: Row[]; total: number; page: number; page_size: number; pages: number }

export default function WorklistPage() {
  const [params, setParams] = useSearchParams()
  const [options, setOptions] = useState<Options | null>(null)
  const [data, setData] = useState<Page | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const get = (k: string, d = '') => params.get(k) ?? d

  // Load the option lists once, then seed the legacy defaults if the URL is
  // bare. `BindFacilityCollection` selects the user's first facility and
  // `BindStatusCollection` selects a status from their permissions.
  useEffect(() => {
    // The seeding is abandoned if the user leaves before the options arrive.
    //
    // `setParams` writes to whatever location is current when it runs, not to
    // the one this page was mounted at, so an unguarded callback rewrites the
    // URL of the page the user navigated *to* — it appends the worklist's
    // filters to it, and can undo the navigation outright. Clicking Reports
    // fast enough bounced straight back to the list.
    let left = false
    apiFetch<Options>('/api/v1/worklist/options')
      .then((o) => {
        if (left) return
        setOptions(o)
        if (!params.has('statusId') && !params.has('facilityId')) {
          const next = new URLSearchParams(params)
          next.set('statusId', String(o.default_status_id))
          next.set('facilityId', String(o.default_facility_id))
          setParams(next, { replace: true })
        }
      })
      .catch((e) => {
        if (!left) setError(e instanceof ApiError ? e.message : String(e))
      })
    return () => {
      left = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams(params)
      if (!qs.has('pageSize')) qs.set('pageSize', '15')
      setData(await apiFetch<Page>(`/api/v1/worklist?${qs.toString()}`))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => {
    if (options) void load()
  }, [options, load])

  function update(patch: Record<string, string>, resetPage = true) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === '' || v === '0') next.delete(k)
      else next.set(k, v)
    }
    if (resetPage) next.delete('page')
    setParams(next)
  }

  function sortBy(column: string) {
    const cur = get('sort', 'StudyDateTime')
    const dir = get('direction', 'DESC')
    update({ sort: column, direction: cur === column && dir === 'DESC' ? 'ASC' : 'DESC' })
  }

  const cols = options?.columns
  // One leading command column (Open), plus Billed when it is drawn, plus the
  // eleven data columns less the three permission 7 hides.
  const columnCount =
    1 +
    (cols?.billed ? 1 : 0) +
    8 +
    (cols?.study_transfer_date ? 1 : 0) +
    (cols?.device ? 1 : 0) +
    (cols?.status ? 1 : 0)
  const sortCol = get('sort', 'StudyDateTime')
  const sortDir = get('direction', 'DESC')
  const caret = (c: string) => (sortCol === c ? (sortDir === 'DESC' ? ' ▾' : ' ▴') : '')

  return (
    <main className="page page-wide">
      {/* `MainStudyList.aspx:18` — "Scan  Reports  Log Off", right-aligned.
          Scan goes to `TestScan.aspx`, which is not reproduced (DEC-023). */}
      <PageNav strip="worklist" />
      <h1>Study List</h1>

      {error && <p className="banner banner-error">{error}</p>}
      {notice && <p className="banner banner-ok">{notice}</p>}

      <fieldset className="filters">
        <legend>Search</legend>
        <div className="filter-row">
          <label>
            Patient ID
            <input
              defaultValue={get('patientId')}
              onBlur={(e) => update({ patientId: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && update({ patientId: e.currentTarget.value })}
            />
          </label>
          <label>
            Patient Name
            <input
              defaultValue={get('patientName')}
              onBlur={(e) => update({ patientName: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && update({ patientName: e.currentTarget.value })}
            />
          </label>
          <label>
            Facility
            <select value={get('facilityId')} onChange={(e) => update({ facilityId: e.target.value })}>
              <option value="">All facilities</option>
              {options?.facilities.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </label>
          <label>
            Study Type
            <select value={get('studyTypeId')} onChange={(e) => update({ studyTypeId: e.target.value })}>
              <option value="">Select a study type</option>
              {options?.study_types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={get('statusId')} onChange={(e) => update({ statusId: e.target.value })}>
              <option value="">All Status</option>
              {options?.statuses.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
        </div>

        {/* The legacy's second search row, behind `chkAdvancedSearch`
            (`MainStudyList.aspx`). The toggle itself is not reproduced — filter
            state lives in the URL here, so a hidden row holding an active filter
            would be the confusing case, and every applied filter should stay
            visible. */}
        <div className="filter-row">
          <label>
            Study Date from
            <input type="date" value={get('start')} onChange={(e) => update({ start: e.target.value })} />
          </label>
          <label>
            to
            <input type="date" value={get('end')} onChange={(e) => update({ end: e.target.value })} />
          </label>
          <label>
            Reading Facility
            <select
              value={get('readingFacilityId')}
              onChange={(e) => update({ readingFacilityId: e.target.value })}
            >
              <option value="">All facilities</option>
              {options?.facilities.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </label>
          <label>
            Interpreting MD
            <select
              value={get('readingPhysicianId')}
              onChange={(e) => update({ readingPhysicianId: e.target.value })}
            >
              <option value="">All Physician</option>
              {options?.reading_physicians.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label>
            Reviewing MD
            <select
              value={get('reviewingPhysicianId')}
              onChange={(e) => update({ reviewingPhysicianId: e.target.value })}
            >
              <option value="">All Physician</option>
              {options?.reviewing_physicians.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label>
            Technician
            <select
              value={get('technicianId')}
              onChange={(e) => update({ technicianId: e.target.value })}
            >
              <option value="">All Technician</option>
              {options?.technicians.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <label>
            E-Signed by
            <select value={get('esign')} onChange={(e) => update({ esign: e.target.value })}>
              <option value="">Select esign status</option>
              {options?.esign.map((e2) => (
                <option key={e2.value} value={e2.value}>{e2.name}</option>
              ))}
            </select>
          </label>
          <label>
            Referring Physician
            <select
              value={get('referringPhysicianId')}
              onChange={(e) => update({ referringPhysicianId: e.target.value })}
            >
              <option value="">All Physician</option>
              {options?.referring_physicians.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          {/* `radYN` — the legacy's Yes/No beside the referring dropdown, which
              flips the predicate from "this physician" to "everyone but". */}
          <label className="check">
            <input
              type="checkbox"
              checked={get('excludeReferring') === 'true'}
              onChange={(e) => update({ excludeReferring: e.target.checked ? 'true' : '' })}
            />
            Exclude this referring physician
          </label>
          <button type="button" onClick={() => setParams(new URLSearchParams())}>
            Clear
          </button>
        </div>
      </fieldset>

      <div className="grid-meta">
        {loading ? 'Loading…' : data ? `${data.total.toLocaleString()} studies` : ''}
        {data && data.pages > 1 && (
          <span className="muted">
            {' '}· page {data.page} of {data.pages.toLocaleString()}
          </span>
        )}
      </div>

      <div className="grid-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th />
              {cols?.billed && <th onClick={() => sortBy('Billed')}>Billed{caret('Billed')}</th>}
              {cols?.study_transfer_date && (
                <th onClick={() => sortBy('StudyDateTime')}>Study Transfer Date{caret('StudyDateTime')}</th>
              )}
              <th onClick={() => sortBy('StudyDate')}>Study Date{caret('StudyDate')}</th>
              {cols?.device && <th onClick={() => sortBy('DeviceName')}>Device{caret('DeviceName')}</th>}
              <th onClick={() => sortBy('PatientID')}>ID#{caret('PatientID')}</th>
              <th onClick={() => sortBy('PatientName')}>Patient Name{caret('PatientName')}</th>
              <th onClick={() => sortBy('StudyTypeID')}>Study Type{caret('StudyTypeID')}</th>
              <th onClick={() => sortBy('ReadingPhysician')}>Interpreting MD{caret('ReadingPhysician')}</th>
              {cols?.status && <th onClick={() => sortBy('Status')}>Status{caret('Status')}</th>}
              <th onClick={() => sortBy('ElectronicallySigned')}>Signed{caret('ElectronicallySigned')}</th>
              <th onClick={() => sortBy('EsignedBy')}>ESigned By{caret('EsignedBy')}</th>
              <th onClick={() => sortBy('ReferringPhysician')}>Referring Physician{caret('ReferringPhysician')}</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <OpenLink id={r.id} qs={params.toString()} />
                </td>
                {cols?.billed && (
                  <td>
                    <BillButton
                      id={r.id}
                      billed={!!r.billed}
                      patient={r.patient_name}
                      onDone={(msg) => {
                        setNotice(msg)
                        void load()
                      }}
                      onError={setError}
                    />
                  </td>
                )}
                {cols?.study_transfer_date && <td>{fmt(r.study_datetime)}</td>}
                <td>{fmtDate(r.study_date)}</td>
                {cols?.device && <td>{r.device_name}</td>}
                <td>{r.patient_id}</td>
                <td>{r.patient_name}</td>
                <td>{r.study_type}</td>
                <td>{r.reading_physician}</td>
                {cols?.status && <td>{r.status}</td>}
                <td>{r.electronically_signed ? 'True' : 'False'}</td>
                <td>{r.esigned_by}</td>
                <td>{r.referring_physician}</td>
              </tr>
            ))}
            {data && data.rows.length === 0 && !loading && (
              <tr><td colSpan={columnCount} className="muted">No studies match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.pages > 1 && (
        <div className="pager">
          <button type="button" disabled={data.page <= 1}
            onClick={() => update({ page: String(data.page - 1) }, false)}>Previous</button>
          <span className="muted">{data.page} / {data.pages.toLocaleString()}</span>
          <button type="button" disabled={data.page >= data.pages}
            onClick={() => update({ page: String(data.page + 1) }, false)}>Next</button>
        </div>
      )}
    </main>
  )
}

function OpenLink({ id, qs }: { id: number; qs: string }) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  // `/open` only decides *where* to go — the legacy's own branch on study type
  // and permission. If it cannot answer, the answer is still Study Information:
  // that is the handler's own `Else` branch, it is the assignment hub, and it is
  // where a study that needs assigning belongs. Blocking the user because a
  // lookup failed is worse than sending them to the right page.
  //
  // An earlier version rendered any failure as the words "no route", which was
  // doubly wrong: it looked like a routing decision the legacy makes (it does
  // not — nothing in the legacy says that), and it hid the actual error. During
  // a database restore every row on the worklist read "no route".
  async function go() {
    setBusy(true)
    const fallback = `/studies/${id}`
    try {
      const target = await apiFetch<OpenTarget>(`/api/v1/studies/${id}/open`)
      navigate(`${target.path || fallback}?${qs}`)
    } catch {
      navigate(`${fallback}?${qs}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button type="button" className="open" disabled={busy} onClick={go}>
      {busy ? '…' : 'Open'}
    </button>
  )
}

// `Case "Billed"` — one `Charges` row per study.
//
// An already-billed study shows the state and no button, which is what the
// legacy grid does: `Datagrid_ItemDataBound` (:616-623) assigns
// `Cells(Billed).Text = ""` when the hidden Billed column reads 1, and assigning
// `TableCell.Text` clears the cell's controls — the LinkButton included.
//
// The confirmation is not in the legacy: it puts one on Delete and Complete
// (`DataGrid_ItemCreated`) and not on Billed, which is part of why 1,056 studies
// are double-billed. No screen here can take a charge back — nothing in the
// application deletes one — so the click is confirmed instead.
function BillButton({
  id,
  billed,
  patient,
  onDone,
  onError,
}: {
  id: number
  billed: boolean
  patient: string | null
  onDone: (msg: string) => void
  onError: (msg: string) => void
}) {
  const [busy, setBusy] = useState(false)

  if (billed) {
    return <span className="muted" title={`Study ${id} has a charge row.`}>Billed</span>
  }

  async function bill() {
    if (!window.confirm(`Bill study ${id}${patient ? ` (${patient})` : ''}? A charge cannot be removed.`)) return
    setBusy(true)
    try {
      const r = await markBilled(id)
      onDone(r.saved ? `Study ${id} billed.` : (r.note ?? `Study ${id} was already billed.`))
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <button type="button" className="open" disabled={busy} onClick={bill}>
      {busy ? '…' : 'Bill'}
    </button>
  )
}

function fmt(v: string | null) {
  if (!v) return ''
  return v.replace('T', ' ').slice(0, 19)
}
function fmtDate(v: string | null) {
  if (!v) return ''
  return v.slice(0, 10)
}
